import { Button, ButtonProps, Flex, Menu, MenuItemProps } from '@mantine/core';
import { PropsWithChildren, type ComponentProps } from 'react';
import styled from 'styled-components';
import { asPlainComponent } from '@dealstack/frontend/shared/utils/asPlainComponent';

const PlainMenuItem = asPlainComponent<ComponentProps<typeof Menu.Item>>(Menu.Item);

type Props = {
	buttonTargetLabel?: string;
	buttonTargetIcon?: React.ReactNode;
	isDisabled?: boolean;
	width?: React.CSSProperties['width'];
	dataTestId?: string;
	buttonVariant?: ButtonProps['variant'];
	buttonColor?: ButtonProps['color'];
} & PropsWithChildren;

export const CapMenu = ({
	children,
	buttonTargetIcon,
	isDisabled,
	buttonTargetLabel,
	width = 240,
	dataTestId,
	buttonColor,
	buttonVariant = 'default',
}: Props) => {
	return (
		<Menu width={width} shadow="md" position="bottom-end" withinPortal>
			<Menu.Target>
				<Button
					variant={buttonVariant}
					disabled={isDisabled}
					color={buttonColor}
					data-testid={dataTestId}
					leftSection={buttonTargetLabel ? buttonTargetIcon : null}
				>
					{buttonTargetLabel ?? buttonTargetIcon}
				</Button>
			</Menu.Target>
			<Menu.Dropdown
				sx={(theme) => ({
					boxShadow: '0px 16px 32px 0px #DCDFE0',
					borderColor: theme.colors.gray[0],
				})}
			>
				<Flex gap="0.25rem" direction="column">
					{children}
				</Flex>
			</Menu.Dropdown>
		</Menu>
	);
};

export const StyledCapMenuItem = styled(PlainMenuItem)<
	MenuItemProps & {
		variant?: string;
		onClick?: () => void;
		closeMenuOnClick?: boolean;
		disabled?: boolean;
	}
>`
	border-radius: 0.25rem;
	background-color: transparent;
	color: ${({ theme, color }) => color ?? theme.v3.colors.dark[8]};

	svg {
		color: ${({ theme, color }) => color ?? theme.v3.colors.dark[8]};
	}

	&:hover {
		background-color: ${({ theme, color }) => color ?? theme.v3.colors.gray[0]};
	}

	&:active,
	&.active {
		background-color: ${({ theme, color }) => color ?? theme.v3.colors.gray[2]};
	}

	&:disabled {
		opacity: 0.4;
	}
`;
